import {
  CrownOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  KeyOutlined,
  LinkOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import { useToggle } from 'ahooks'
import { Avatar, Badge, Button, Popover, Tooltip, Typography } from 'antd'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { UserHoverCard, UserListModal } from '@/components'
import { EventInfoFragment, useGetEventQuery } from '@/generated/graphql'
import { routes } from '@/routes'

import { EventActions } from './event-card-actions.component'
import s from './event-card.module.less'

export const EventCard: React.FC<{ data: EventInfoFragment }> = ({ data, children }) => {
  const participantDisplayCount = 4
  const { t } = useTranslation()
  const [visible, { toggle }] = useToggle()
  const { data: eventData } = useGetEventQuery({
    variables: {
      eventId: data.eventId,
    },
  })

  if (!eventData) {
    return null
  }

  const event = eventData.event

  const EventCardItem = (icon: React.ReactNode, content: React.ReactNode) => {
    return (
      <div className={s.item}>
        <div className={s.icon}>{icon}</div>
        <div className={s.content}>{content}</div>
      </div>
    )
  }

  const title = EventCardItem(
    <Badge size='default' color={event.isParticipated ? '#6FA960' : event.isInterested ? '#F34616' : 'purple'} />,
    <div>
      <Typography.Title style={{ marginBottom: 0, marginTop: 4 }} level={5}>
        {event.topic}
      </Typography.Title>
      <span>{`${moment(event.startTime).format('MMMM Do (dddd) ⋅ a h:mm')}
      -
      ${
        moment(event.endTime).format(' a ') === moment(event.startTime).format(' a ')
          ? ''
          : moment(event.endTime).format(' a ')
      }
      ${moment(event.endTime).format('h:mm')}`}</span>
    </div>
  )

  const location = EventCardItem(<EnvironmentOutlined />, <span>{event.location}</span>)

  const guests = EventCardItem(
    <Tooltip title='嘉宾'>
      <CrownOutlined />
    </Tooltip>,
    <div>
      {event.guests.map((guest) => (
        <UserHoverCard key={guest.user.userId} email={guest.user.email}>
          <Link key={guest.user.userId} style={{ marginRight: 8 }} to={routes.users.page(guest.user.email)}>
            {guest.user.name}
          </Link>
        </UserHoverCard>
      ))}
      {event.foreignGuests}
    </div>
  )

  const users = event.participants.edges.map(({ node }) => node)
  const participantTotalCount = event.participants.totalCount
  const participants = EventCardItem(
    <TeamOutlined />,
    <div className={s.participants}>
      {users.slice(0, participantDisplayCount).map((participant) => (
        <UserHoverCard key={participant.userId} email={participant.email}>
          <Avatar size={22} src={participant.avatarUrl} />
        </UserHoverCard>
      ))}
      {participantTotalCount > participantDisplayCount && (
        <div onClick={() => toggle()}>
          <UserListModal
            title={t('calendar.participant')}
            eventId={event.eventId}
            type='eventParticipants'
            paginationInput={{ sortBy: 'updatedAt', useFirst: true }}
            showIndex={true}
          >
            <Button type='link'>{`+${participantTotalCount - participantDisplayCount}`}</Button>
          </UserListModal>
        </div>
      )}
    </div>
  )

  const actions = <EventActions data={data} />

  const avcLink =
    event.avcLink &&
    EventCardItem(
      <Tooltip title={t('calendar.avcLink')}>
        <LinkOutlined />
      </Tooltip>,
      <a href={event.avcLink} target='_blank' rel='noopener noreferrer'>
        {event.avcLink}
      </a>
    )

  const avcRoom =
    !event.avcLink &&
    event.avcRoom &&
    EventCardItem(
      <Tooltip title={t('calendar.avcRoom')}>
        <HomeOutlined />
      </Tooltip>,
      <div>
        <span>{event.avcRoom}</span>
        <Typography.Text copyable={{ text: event.avcRoom }} />
      </div>
    )

  const avcPassword =
    !event.avcLink &&
    event.avcRoom &&
    EventCardItem(
      <Tooltip title={t('calendar.avcPassword')}>
        <KeyOutlined />
      </Tooltip>,
      <div>
        <span>{event.avcPassword}</span>
        {event.avcPassword && <Typography.Text copyable={{ text: event.avcPassword }} />}
      </div>
    )

  const content = (
    <div className={s.container} onClick={(e) => e.stopPropagation()}>
      {title}
      {guests}
      {location}
      {avcRoom}
      {avcPassword}
      {avcLink}
      {participantTotalCount > 0 && participants}
      {actions}
    </div>
  )

  return (
    <Popover {...(visible && { visible: false })} placement='left' content={content}>
      {children}
    </Popover>
  )
}
